<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { NxButton, NxCodeBlock, NxList, NxListRow, NxModal, useNxToasts } from '@blpde/nx-ui';
import {
  useSearchDictionaryClient,
  type DictionarySource,
  type DictionarySources,
} from '../../search/dictionaryApi';
import { DICTIONARY_TEXT } from './labels';

/**
 * The base vocabulary's attribution under the dictionary and its licence
 * notices in a modal (`GET /search/dictionary/sources`, `/licences/:name`):
 * what the search's shipped words and synonyms are, and under which licence.
 * Reads once; a failed read leaves the line out.
 */
const { t, locale } = useI18n();
const { showToast } = useNxToasts();
const client = useSearchDictionaryClient();
const T = DICTIONARY_TEXT;

const open = ref(false);
const body = ref<DictionarySources | null>(null);
/** Notice text loaded from `noticeUrl`. Keyed by that path. */
const loadedNotices = ref<Record<string, string>>({});
const busy = ref<Record<string, boolean>>({});

function shownNotice(source: DictionarySource): string {
  if (source.notice) return source.notice;
  if (!source.noticeUrl) return '';
  return loadedNotices.value[source.noticeUrl] ?? '';
}

async function loadNotice(source: DictionarySource): Promise<void> {
  const url = source.noticeUrl;
  if (!url || source.notice || busy.value[url]) return;
  busy.value = { ...busy.value, [url]: true };
  try {
    const text = await client.licenceNotice(url);
    loadedNotices.value = { ...loadedNotices.value, [url]: text };
  } catch {
    showToast(t(`${T}.sources.licenceError`), 'error');
  } finally {
    const next = { ...busy.value };
    delete next[url];
    busy.value = next;
  }
}

const attribution = computed(() => {
  const text = body.value?.attribution;
  if (!text) return '';
  const lang = locale.value.startsWith('en') ? 'en' : 'de';
  return text[lang] || text.de || text.en || '';
});

onMounted(async () => {
  try {
    body.value = await client.sources();
  } catch {
    body.value = null;
  }
});
</script>

<template>
  <p v-if="attribution" class="dict-note" data-testid="dictionary-attribution">
    {{ attribution }}
    <NxButton variant="link" data-testid="dictionary-licences" @click="open = true">
      {{ t(`${T}.sources.licences`) }}
    </NxButton>
  </p>
  <NxModal v-model:open="open" :title="t(`${T}.sources.licences`)" size="wide">
    <template #body>
      <NxList data-testid="dictionary-licence-list">
        <NxListRow
          v-for="(source, index) in body?.sources ?? []"
          :key="`${source.name}-${index}`"
          :title="source.name"
          :subtitle="[source.author, source.version, source.licence].filter(Boolean).join(' · ')"
          :meta="source.use"
          :href="source.url || undefined"
          :clickable="Boolean(source.url)"
          wrap
        >
          <template v-if="source.notice || source.noticeUrl" #footer>
            <NxCodeBlock
              v-if="shownNotice(source)"
              :code="shownNotice(source)"
              wrap
              max-height="var(--nxt-panel-width-lg)"
              :aria-label="source.name"
            />
            <NxButton
              v-else
              variant="link"
              :loading="Boolean(source.noticeUrl && busy[source.noticeUrl])"
              data-testid="dictionary-licence-load"
              @click="loadNotice(source)"
            >
              {{ t(`${T}.sources.showLicence`) }}
            </NxButton>
          </template>
        </NxListRow>
      </NxList>
    </template>
  </NxModal>
</template>

<style scoped>
.dict-note {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
</style>
